import { Box, Paper, Tooltip, Typography, useTheme } from "@mui/material";
import { useMemo, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FiFilter, FiRefreshCw, FiX } from "react-icons/fi";
import AddToSupplyChainButton from "../../../components/button/AddToSuplyChainButton";
import PivotableBiaxialBarChart from "../../../components/chart/PivotableBiaxialBarChart";
import PivotablePieChart from "../../../components/chart/PivotablePieChart";
import ScatterChart from "../../../components/chart/ScatterChart";
import ResizableBox from "../../../components/container/ResizableBox";
import StandardCardContainer from "../../../components/container/StandardCardContainer";
import { TableFooterCheckBoxesContainer } from "../../../components/container/TableFooterCheckBoxesContainer";
import { TableFooterContainer } from "../../../components/container/TableFooterContainer";
import ColumnMenu from "../../../components/context-menu/ColumnMenu";
import AgGrid from "../../../components/grid/AgGrid";
import {
  centerAlignColDef,
  defaultColDef,
  floatFormater,
  leftAlignColDef,
  rightAlignColDef,
  translationFormater,
} from "../../../components/grid/columnDefinitions/columnDefinitions";
import CheckBox from "../../../components/input/CheckBox";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
import { TableHeaderContainer } from "../../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../../components/container/TableTitleContainer";
import { TableHeaderToolsContainer } from "../../../components/container/TableHeaderToolsContainer";
import SearchBox from "../../../components/input/SearchBox";
import ColumnVisibilityButton from "../../../components/button/ColumnVisibilityButton";
import RefreshButton from "../../../components/button/RefreshButton";
import ExportAsXLSXButton from "../../../components/export/ExportAsXLSXButton";
import { GridPagination } from "../../../components/grid/AgGrid";
import { Supplier_Reports } from "../../../db_access/supplier_reports";
import { Supply_chain } from "../../../db_access/supply_chain";
import { ReportTypes } from "../ReportTypes";
import { getOrgTokens } from "../../../theme/style/organizationPalette";

const ReportDetailGrid = ({ companies, selectedCompanyIds, emissionWeight, showAddButton = true, setSelectedCompanyIds }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const { columnVisibilities, setRefresh } = useContextProvider();
  const table_ref = useRef();
  const header_ref = useRef();
  const colVisibility_key = "supplier_directory_dashboard_report_table";
  const [showColVisibilityMenu, setShowColVisibilityMenu] = useState(false);
  const [graphView, setGraphView] = useState(false);
  const [selectedReportIds, setSelectedReportIds] = useState([]);
  const [searchText, setSearchText] = useState("");
  const addToSupplyChain = () => {
    console.log(
      "Add to Supply Chain",
      selectedCompanyIds,
      selectedReportIds,
      companies.filter((c) => selectedCompanyIds.includes(c.id)),
      companies.find((c) => selectedCompanyIds.includes(c.id))?.reports.filter((r) => selectedReportIds.includes(r.id))
    );
    Supply_chain.add_from_supplier_report(selectedReportIds).then(() => setRefresh((r) => !r));
  };

  const columnDefs = useMemo(() => {
    const isHidden = (field) => !columnVisibilities[colVisibility_key]?.includes(field);
    const onCellValueChanged = (params) => {
      const { data } = params;
      console.log(params);
      Supplier_Reports.update_type(data.id, data.type).finally(() => setRefresh((r) => !r));
    };
    const colDefs = [
      { field: "name", headerName: t("Name"), ...leftAlignColDef, ...defaultColDef },
      { field: "year", headerName: t("Year"), ...centerAlignColDef, ...defaultColDef },
      { field: "quantity", headerName: t("Quantity"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { field: "cost", headerName: t("Cost"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { field: "cost_per_unit", headerName: t("Cost Per Unit"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { field: "gwp", headerName: t("GWP"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { field: "gwp_per_unit", headerName: t("GWP Per Unit"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { field: "functional_unit", headerName: t("Functional Unit"), ...centerAlignColDef, ...defaultColDef },
      { field: "other_unit", headerName: t("Other Unit"), ...centerAlignColDef, ...defaultColDef },
      {
        field: "type",
        headerName: t("Type"),
        ...centerAlignColDef,
        ...defaultColDef,
        editable: true,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: ReportTypes },
        ...translationFormater(t),
        onCellValueChanged: (params) => onCellValueChanged(params),
      },
      { field: "level", headerName: t("Level"), ...centerAlignColDef, ...defaultColDef, ...translationFormater(t) },
      { field: "scope", headerName: t("Scope"), ...centerAlignColDef, ...defaultColDef, ...translationFormater(t) },
      { field: "last_modified", headerName: t("Last Modified"), ...rightAlignColDef, ...defaultColDef },
      { field: "created_at", headerName: t("Created At"), ...rightAlignColDef, ...defaultColDef },
    ];
    return colDefs.map((col) => {
      if (col.editable) {
        return { ...col, onCellValueChanged, hide: isHidden(col.field) };
      }
      return { ...col, hide: isHidden(col.field) };
    });
  }, [columnVisibilities, t, setRefresh]);

  useEffect(() => {
    if (table_ref.current?.api) {
      table_ref.current.api.setGridOption("quickFilterText", searchText);
    }
  }, [searchText]);

  const currentReports = useMemo(() => {
    return companies.find((c) => c.id === selectedCompanyIds[0])?.reports || [];
  }, [companies, selectedCompanyIds]);

  const totalMetrics = useMemo(() => {
    const totalGwp = currentReports.reduce((sum, r) => sum + (Number(r.gwp) || 0), 0);
    const totalCost = currentReports.reduce((sum, r) => sum + (Number(r.cost) || 0), 0);
    return {
      gwp: totalGwp.toLocaleString(undefined, { maximumFractionDigits: 0 }),
      cost: totalCost.toLocaleString(undefined, { maximumFractionDigits: 0 })
    };
  }, [currentReports]);

  return (
    <Box sx={{ display: selectedCompanyIds.length === 1 ? "block" : "none", mt: 4 }}>
      <Paper variant="Table Container">
        <TableHeaderContainer>
          <TableTitleContainer>
            <Typography variant="Table Title">
              {companies.find((c) => c.id === selectedCompanyIds[0])?.name} - {t("Granular Submission Breakdown")} ({currentReports.length})
            </Typography>
          </TableTitleContainer>
          <TableHeaderToolsContainer ref={header_ref}>
            <SearchBox text={searchText} setText={setSearchText} placeholder={t("Search reports...")} />
            <ColumnVisibilityButton onClick={() => setShowColVisibilityMenu((p) => !p)} />
            <RefreshButton setRefresh={setRefresh} />
            <Box onClick={() => setSelectedCompanyIds && setSelectedCompanyIds([])} sx={{ p: 1, ml: 1, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "10px", "&:hover": { bgcolor: "action.hover" } }}>
              <FiX size={18} color="#64748b" />
            </Box>
          </TableHeaderToolsContainer>
        </TableHeaderContainer>
        <Box>
          <AgGrid
            ref={table_ref}
            rowData={currentReports}
            columnDefs={columnDefs}
            onSelectionChanged={(event) => {
              setSelectedReportIds(event.api.getSelectedRows().map((r) => r.id));
            }}
            domLayout="autoHeight"
            footerLeftContent={
              <Box sx={{ display: 'flex', alignItems: 'center', ml: -1 }}>
                <CheckBox 
                  checked={graphView} 
                  onChange={(e) => setGraphView(e.target.checked)} 
                  label={t("Global View")} 
                />
              </Box>
            }
            footerRightContent={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Box sx={{ display: showAddButton ? "block" : "none" }}>
                  <AddToSupplyChainButton 
                    disabled={selectedReportIds.length === 0} 
                    onClick={addToSupplyChain}
                    sx={{
                      height: 44,
                      px: 4,
                      borderRadius: "12px",
                      boxShadow: selectedReportIds.length > 0 ? "0 4px 14px rgba(37, 99, 235, 0.25)" : "none",
                      backgroundColor: "#2563eb",
                      "&:hover": { backgroundColor: "#1d4ed8" }
                    }}
                  />
                </Box>
              </Box>
            }
          />
        </Box>
      </Paper>

      {/* ── Independent Visualization Components ── */}
      <Box sx={{ display: graphView ? "flex" : "none", flexDirection: "column", gap: 3, mt: 3 }}>
        <Typography variant="Table Title" sx={{ px: 1, mt: 3, mb: 1, color: tok.text.primary, fontWeight: 500 }}>
          {t("Portfolio Analytics")}
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3 }}>
          <StandardCardContainer minHeight={450}>
            <PivotablePieChart 
              usages={currentReports} 
              title={t("Emission Breakdown by Type")} 
              groupBy={"type"} 
              countBy={"gwp"} 
            />
          </StandardCardContainer>
          <StandardCardContainer minHeight={450}>
            <PivotablePieChart 
              usages={currentReports} 
              title={t("Cost Breakdown by Type")} 
              groupBy={"type"} 
              countBy={"cost"} 
            />
          </StandardCardContainer>
        </Box>

        <StandardCardContainer minHeight={500}>
          <PivotableBiaxialBarChart
            usages={currentReports}
            title={t("Comparative GWP & Cost Analysis")}
            groupBy={"type"}
            countBys={[
              { name: "gwp", axis: "left", label: t("GWP") },
              { name: "cost", axis: "right", label: t("Cost") },
            ]}
          />
        </StandardCardContainer>
      </Box>
      <ColumnMenu
        anchorRef={header_ref}
        visible={showColVisibilityMenu}
        onClose={() => setShowColVisibilityMenu(false)}
        colDefs={columnDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={["name", "year", "quantity", "cost", "cost_per_unit", "gwp", "gwp_per_unit", "type", "level", "scope", "last_modified"]}
        gridRef={table_ref}
      />
    </Box>
  );
};

export default ReportDetailGrid;
